/* Sleep LP Clock — interactive circadian clock
   Vertical track from top-hour (top) to bottom-hour (bottom) going
   backwards through midnight (e.g. 6 AM → 12 AM → 6 PM). Two
   draggable handles set sleep + wake times. */
(function () {
  'use strict';

  var STATUSES = {
    compromised: { id: 'compromised', label: 'Compromised' },
    borderline: { id: 'borderline', label: 'Borderline' },
    optimal: { id: 'optimal', label: 'Optimal' }
  };

  function classifyHours(hours) {
    if (hours < 6.5) return STATUSES.compromised;
    if (hours < 7.5) return STATUSES.borderline;
    return STATUSES.optimal;
  }

  function formatTime(minutes) {
    minutes = ((Math.round(minutes) % 1440) + 1440) % 1440;
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    var period = h >= 12 ? 'p.m.' : 'a.m.';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    var mm = (m < 10 ? '0' : '') + m;
    return h12 + ':' + mm + ' ' + period;
  }

  function formatHours(durationMin) {
    var h = durationMin / 60;
    if (h >= 8) return '8+';
    return (Math.round(h * 10) / 10).toString();
  }

  function cyclesText(durationMin) {
    var cycles = durationMin / 90;
    var lo = Math.floor(cycles);
    var hi = Math.ceil(cycles);
    if (lo === hi) return lo + ' full cycles';
    return lo + '–' + hi + ' full cycles';
  }

  function initClock(root) {
    var topHour = parseInt(root.dataset.clockTopHour, 10);
    var bottomHour = parseInt(root.dataset.clockBottomHour, 10);
    var snap = parseInt(root.dataset.snapMinutes, 10) || 15;
    if (isNaN(topHour)) topHour = 6;
    if (isNaN(bottomHour)) bottomHour = 18;

    var topMin = topHour * 60;
    var bottomMin = bottomHour * 60;
    var spanMin = ((topMin - bottomMin) + 1440) % 1440;
    if (spanMin === 0) spanMin = 1440;

    function minutesToPos(min) {
      var offset = ((topMin - min) + 1440) % 1440;
      return Math.max(0, Math.min(1, offset / spanMin));
    }
    function posToMinutes(pos) {
      pos = Math.max(0, Math.min(1, pos));
      var m = topMin - pos * spanMin;
      if (m < 0) m += 1440;
      var snapped = Math.round(m / snap) * snap;
      return ((snapped % 1440) + 1440) % 1440;
    }

    // State
    var state = {
      sleep: parseInt(root.dataset.defaultSleep, 10),
      wake: parseInt(root.dataset.defaultWake, 10)
    };
    if (isNaN(state.sleep)) state.sleep = 1320;
    if (isNaN(state.wake)) state.wake = 330;

    var track = root.querySelector('[data-clock-track]');
    var ticksEl = root.querySelector('[data-clock-ticks]');
    var windowEl = root.querySelector('[data-clock-window]');
    var handles = {
      sleep: root.querySelector('[data-clock-handle="sleep"]'),
      wake: root.querySelector('[data-clock-handle="wake"]')
    };
    var sleepTimeEl = root.querySelector('[data-clock-sleep-time]');
    var wakeTimeEl = root.querySelector('[data-clock-wake-time]');
    var statusChipEl = root.querySelector('[data-clock-status]');
    var statusChipLabelEl = root.querySelector('[data-clock-status-label]');
    var statusCardEl = root.querySelector('[data-clock-status-card]');
    var statusCardLabelEl = root.querySelector('[data-clock-status-card-label]');
    var statusCardHoursEl = root.querySelector('[data-clock-hours]');
    var statusCardNoteEl = root.querySelector('[data-clock-status-card-note]');

    // Generate tick marks (one every 30 min)
    if (ticksEl) {
      var tickCount = Math.round(spanMin / 30);
      ticksEl.innerHTML = '';
      for (var i = 0; i <= tickCount; i++) {
        ticksEl.appendChild(document.createElement('span'));
      }
      // distribute via flex justify-content: space-between; height = 1
    }

    function durationMin() {
      return ((state.wake - state.sleep) + 1440) % 1440;
    }

    function render() {
      if (sleepTimeEl) sleepTimeEl.textContent = formatTime(state.sleep);
      if (wakeTimeEl) wakeTimeEl.textContent = formatTime(state.wake);

      var sleepPos = minutesToPos(state.sleep);
      var wakePos = minutesToPos(state.wake);

      // 12-hour analog dial mapping: 12 at top, clockwise. Both AM and PM
      // collapse into the same 0-720 minute range so the marker sits at
      // the actual clock-face hour (e.g. 6 PM → "6" at the bottom).
      var sleepCircle = (state.sleep % 720) / 720;
      var wakeCircle = (state.wake % 720) / 720;
      var useCircle = track && track.classList.contains('is-circular');
      var sleepRenderPos = useCircle ? sleepCircle : sleepPos;
      var wakeRenderPos = useCircle ? wakeCircle : wakePos;

      if (handles.sleep) {
        handles.sleep.style.top = useCircle ? '' : (sleepPos * 100) + '%';
        handles.sleep.style.setProperty('--pos', sleepRenderPos);
      }
      if (handles.wake) {
        handles.wake.style.top = useCircle ? '' : (wakePos * 100) + '%';
        handles.wake.style.setProperty('--pos', wakeRenderPos);
      }

      if (windowEl) {
        var top = Math.min(sleepPos, wakePos);
        var bottom = Math.max(sleepPos, wakePos);
        windowEl.style.top = (top * 100) + '%';
        windowEl.style.height = ((bottom - top) * 100) + '%';
      }

      if (track) {
        // For the circular arc (sleep → wake going clockwise on a 24h dial)
        var arcLen = ((wakeCircle - sleepCircle) + 1) % 1;
        track.style.setProperty('--sleep-deg', (sleepCircle * 360) + 'deg');
        track.style.setProperty('--arc-deg', (arcLen * 360) + 'deg');
      }

      var hrs = durationMin() / 60;
      var status = classifyHours(hrs);
      root.dataset.state = status.id;
      if (statusChipEl) statusChipEl.dataset.state = status.id;
      if (statusCardEl) statusCardEl.dataset.state = status.id;
      if (statusChipLabelEl) statusChipLabelEl.textContent = status.label;
      if (statusCardLabelEl) statusCardLabelEl.textContent = status.label;
      if (statusCardHoursEl) statusCardHoursEl.textContent = formatHours(durationMin());
      if (statusCardNoteEl) statusCardNoteEl.textContent = cyclesText(durationMin());
    }

    function isCircular() {
      return track && track.classList.contains('is-circular');
    }

    // Drag
    function startDrag(handleKey) {
      return function (e) {
        e.preventDefault();
        var handle = handles[handleKey];
        if (!handle) return;
        handle.setPointerCapture && handle.setPointerCapture(e.pointerId);

        var rect = track.getBoundingClientRect();

        // Circular drag bookkeeping: track direction so each sweep past
        // 12 (top) advances/retreats the AM/PM bucket. Keeps the time
        // continuous on a 12-hour dial.
        var lastCirclePos = null;
        var bucket = Math.floor(state[handleKey] / 720); // 0 = AM, 1 = PM

        function update(clientX, clientY) {
          var newMin;
          if (isCircular()) {
            var cx = rect.left + rect.width / 2;
            var cy = rect.top + rect.height / 2;
            var dx = clientX - cx;
            var dy = clientY - cy;
            var angle = Math.atan2(dy, dx) + Math.PI / 2; // 0 at top, increases clockwise
            if (angle < 0) angle += Math.PI * 2;
            var pos = angle / (Math.PI * 2); // 0..1 around dial

            if (lastCirclePos !== null) {
              var delta = pos - lastCirclePos;
              if (delta > 0.5) bucket -= 1;        // wrapped past 12 going counter-clockwise
              else if (delta < -0.5) bucket += 1;  // wrapped past 12 going clockwise
            }
            lastCirclePos = pos;

            var b = ((bucket % 2) + 2) % 2;
            var minutes = b * 720 + pos * 720;
            var snapped = Math.round(minutes / snap) * snap;
            newMin = ((snapped % 1440) + 1440) % 1440;
          } else {
            var vpos = (clientY - rect.top) / rect.height;
            newMin = posToMinutes(vpos);
          }
          state[handleKey] = newMin;
          render();
        }
        update(e.clientX, e.clientY);

        function onMove(ev) { update(ev.clientX, ev.clientY); }
        function onUp(ev) {
          handle.removeEventListener('pointermove', onMove);
          handle.removeEventListener('pointerup', onUp);
          handle.removeEventListener('pointercancel', onUp);
        }
        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onUp);
      };
    }

    // Toggle .is-circular based on viewport, swap labels (12/3/6/9 on circle),
    // re-render so handles + arc reposition with the new mapping.
    var CIRCLE_LABELS = { '0': '12', '0.25': '3', '0.5': '6', '0.75': '9', '1': '12' };
    var labelEls = root.querySelectorAll('[data-clock-label-pos]');
    labelEls.forEach(function (el) { el.dataset.origText = el.textContent.trim(); });

    function applyLabels(circular) {
      labelEls.forEach(function (el) {
        var key = el.getAttribute('data-clock-label-pos');
        if (circular) el.textContent = CIRCLE_LABELS[key] != null ? CIRCLE_LABELS[key] : el.dataset.origText;
        else el.textContent = el.dataset.origText;
      });
    }

    var circularMQ = window.matchMedia('(min-width: 1024px)');
    function syncShape() {
      if (!track) return;
      var circular = circularMQ.matches;
      if (circular) track.classList.add('is-circular');
      else track.classList.remove('is-circular');
      applyLabels(circular);
      render();
    }
    syncShape();
    if (circularMQ.addEventListener) circularMQ.addEventListener('change', syncShape);
    else circularMQ.addListener(syncShape);

    if (handles.sleep) handles.sleep.addEventListener('pointerdown', startDrag('sleep'));
    if (handles.wake) handles.wake.addEventListener('pointerdown', startDrag('wake'));

    // Keyboard support
    function keyHandler(handleKey) {
      return function (e) {
        var delta = 0;
        if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') delta = -snap;
        else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') delta = snap;
        if (delta === 0) return;
        e.preventDefault();
        state[handleKey] = ((state[handleKey] + delta) + 1440) % 1440;
        render();
      };
    }
    if (handles.sleep) handles.sleep.addEventListener('keydown', keyHandler('sleep'));
    if (handles.wake) handles.wake.addEventListener('keydown', keyHandler('wake'));

    // Sources modal
    var openBtn = root.querySelector('[data-clock-sources-open]');
    var modal = root.querySelector('[data-clock-sources-modal]');
    var closeEls = root.querySelectorAll('[data-clock-sources-close]');
    function openModal() { if (modal) { modal.hidden = false; document.body.style.overflow = 'hidden'; } }
    function closeModal() { if (modal) { modal.hidden = true; document.body.style.overflow = ''; } }
    if (openBtn) openBtn.addEventListener('click', openModal);
    closeEls.forEach(function (el) { el.addEventListener('click', closeModal); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
    });

    render();
  }

  function init() {
    document.querySelectorAll('[data-sleep-clock]').forEach(initClock);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-sleep-clock]');
    if (root) initClock(root);
  });
})();
